import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";

const pmt = (principal, aprPct, months) => {
  const r = aprPct / 100 / 12;
  if (r === 0) return principal / months;
  return (principal * r) / (1 - Math.pow(1 + r, -months));
};

const money = (n) => `$${Math.round(n).toLocaleString()}`;

// Illustrative SBA 7(a) / 504 terms — average max rates and standard amortization windows.
const TERM_PRESETS = [
  { id: "wc", label: "Working capital · 10 yr", months: 120, defaultRate: 11.5 },
  { id: "equip", label: "Equipment · 15 yr", months: 180, defaultRate: 11.0 },
  { id: "re", label: "Real estate · 25 yr", months: 300, defaultRate: 10.5 },
];

const ELIGIBILITY = [
  { label: "US-based for-profit business", pass: true },
  { label: "Owner-operator, 20%+ ownership", pass: true },
  { label: "Time in business — 24+ months preferred", pass: true },
  { label: "Personal FICO 680+ (600–679 case-by-case)", pass: true },
  { label: "No open bankruptcies or federal debt delinquencies", pass: true },
];

export const SBAPaymentCalculator = () => {
  const [amount, setAmount] = useState(500000);
  const [termId, setTermId] = useState("re");
  const [rate, setRate] = useState(10.5);

  const term = TERM_PRESETS.find((t) => t.id === termId) || TERM_PRESETS[0];

  const { monthly, totalInterest, totalRepayment } = useMemo(() => {
    const m = pmt(amount, rate, term.months);
    const total = m * term.months;
    return {
      monthly: m,
      totalInterest: total - amount,
      totalRepayment: total,
    };
  }, [amount, rate, term]);

  const selectTerm = (id) => {
    const preset = TERM_PRESETS.find((t) => t.id === id);
    setTermId(id);
    if (preset) setRate(preset.defaultRate);
  };

  return (
    <section
      id="sba-calculator"
      className="py-32 md:py-44 border-t border-black/10"
      style={{ background: "linear-gradient(180deg, rgba(196,164,124,0.04), transparent 60%)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8 items-end mb-12">
          <div className="col-span-12 md:col-span-7">
            <div className="overline">SBA loan estimator · Payment & eligibility</div>
            <h2 className="mt-4 font-serif text-5xl md:text-6xl leading-[0.95]">
              What does an <em className="italic text-[color:var(--gold)]">SBA loan</em> actually cost?
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5">
            <p className="text-[color:var(--bone)]/75 leading-relaxed">
              SBA loans are the cheapest business capital available — but only if you qualify. Estimate the monthly payment across common terms, then check the eligibility box beside it. Numbers are illustrative averages, not offers.
            </p>
          </div>
        </div>

        <div className="border border-black/10 bg-[color:var(--bg-elev)] p-8 md:p-12">
          <div className="grid grid-cols-12 gap-8">
            {/* Inputs */}
            <div className="col-span-12 md:col-span-5 space-y-10">
              {/* Amount */}
              <div>
                <div className="flex justify-between items-baseline mb-3">
                  <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/60">
                    Loan amount
                  </label>
                  <div data-testid="sba-amount-display" className="font-serif text-3xl text-[color:var(--gold)]">
                    {amount >= 1_000_000
                      ? `$${(amount / 1_000_000).toFixed(amount % 1_000_000 === 0 ? 0 : 2)}M`
                      : `$${(amount / 1000).toFixed(0)}K`}
                  </div>
                </div>
                <input
                  data-testid="sba-amount-slider"
                  type="range"
                  min={50000}
                  max={5000000}
                  step={25000}
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className="w-full estimator-slider"
                  style={{ "--val": `${((amount - 50000) / (5000000 - 50000)) * 100}%` }}
                  aria-label="Loan amount"
                />
                <div className="mt-2 flex justify-between font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--bone)]/40">
                  <span>$50K</span>
                  <span>$5M</span>
                </div>
              </div>

              {/* Term */}
              <div>
                <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/60 mb-3 block">
                  Use of funds & term
                </label>
                <div className="flex flex-col gap-2">
                  {TERM_PRESETS.map((t) => (
                    <button
                      key={t.id}
                      data-testid={`sba-term-${t.id}`}
                      onClick={() => selectTerm(t.id)}
                      className={`text-left px-4 py-3 border font-mono text-[11px] tracking-[0.22em] uppercase transition-all ${
                        termId === t.id
                          ? "border-[color:var(--gold)] bg-[color:var(--gold)] text-black"
                          : "border-[color:var(--bone)]/20 text-[color:var(--bone)]/75 hover:border-[color:var(--gold)] hover:text-[color:var(--gold)]"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Rate */}
              <div>
                <div className="flex justify-between items-baseline mb-3">
                  <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/60">
                    Estimated rate (APR)
                  </label>
                  <div data-testid="sba-rate-display" className="font-serif text-3xl text-[color:var(--gold)]">
                    {rate.toFixed(2)}%
                  </div>
                </div>
                <input
                  data-testid="sba-rate-slider"
                  type="range"
                  min={9}
                  max={14}
                  step={0.1}
                  value={rate}
                  onChange={(e) => setRate(Number(e.target.value))}
                  className="w-full estimator-slider"
                  style={{ "--val": `${((rate - 9) / (14 - 9)) * 100}%` }}
                  aria-label="Interest rate"
                />
                <div className="mt-2 flex justify-between font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--bone)]/40">
                  <span>9%</span>
                  <span>14%</span>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="col-span-12 md:col-span-7 md:pl-12 md:border-l border-black/10">
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50 mb-8">
                The monthly reality
              </div>

              <div className="grid grid-cols-2 gap-6 mb-10">
                <div className="border-t-2 border-[color:var(--gold)] pt-5 col-span-2">
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--gold)]">
                    Estimated monthly payment
                  </div>
                  <motion.div
                    data-testid="sba-monthly"
                    key={Math.round(monthly)}
                    initial={{ opacity: 0.4 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.35 }}
                    className="mt-2 font-serif text-5xl md:text-6xl text-[color:var(--gold)]"
                  >
                    {money(monthly)}
                  </motion.div>
                </div>
                <div className="border-t border-black/10 pt-5">
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/55">
                    Total interest
                  </div>
                  <div data-testid="sba-total-interest" className="mt-2 font-serif text-3xl md:text-4xl text-[color:var(--bone)]">
                    {money(totalInterest)}
                  </div>
                </div>
                <div className="border-t border-black/10 pt-5">
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/55">
                    Total repayment
                  </div>
                  <div data-testid="sba-total-repayment" className="mt-2 font-serif text-3xl md:text-4xl text-[color:var(--bone)]">
                    {money(totalRepayment)}
                  </div>
                </div>
              </div>

              {/* Eligibility */}
              <div className="border-t border-black/10 pt-8">
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50 mb-5">
                  Baseline eligibility · SBA 7(a)
                </div>
                <ul data-testid="sba-eligibility-list" className="space-y-3">
                  {ELIGIBILITY.map((item) => (
                    <li key={item.label} className="flex items-start gap-3 text-[color:var(--bone)]/80 text-sm leading-relaxed">
                      <Check size={16} className="mt-1 flex-shrink-0 text-[color:var(--gold)]" />
                      <span>{item.label}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Link
                  to="/apply"
                  className="inline-flex items-center gap-3 bg-[color:var(--gold)] text-black px-6 py-3 font-mono text-xs tracking-[0.24em] uppercase hover:bg-[color:var(--gold-strong)] transition-colors"
                >
                  Start SBA application <ArrowRight size={14} />
                </Link>
                <Link
                  to="/blog/category/sba"
                  className="gold-link font-mono text-xs tracking-[0.24em] uppercase text-[color:var(--gold)]"
                >
                  Read the SBA field notes →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SBAPaymentCalculator;
